/**
 * Field catalog v2 for the Mieterselbstauskunft generator
 * (Sanierungs-Spec 2026-07-06 §2.2, Produkt-Pivot E3).
 *
 * There is ONE Mieterselbstauskunft. WHEN the landlord asks for it is his
 * decision – the catalog ADVISES instead of gating:
 *
 *  - green  = harmless, may be asked at any time;
 *  - yellow = admissible, but recommended only from a later point in the
 *    process (`advice.recommendedFrom` + a Du-Form `reasonDe` for the UI);
 *  - red    = AGG/GDPR hard taboos. They exist VISIBLY as
 *    `FORBIDDEN_CATEGORIES` (rendered in the UI catalog as "Das darfst du
 *    nicht fragen"), but structurally never as fields – the NEVER-generate
 *    guarantee is asserted by `__tests__/nie-list.test.ts` (18 rules, DE+EN
 *    patterns) against this catalog AND the rendered PDF.
 *
 * The DSK phase model survives only as (a) optional UI presets
 * (`SELF_DISCLOSURE_PRESETS`, §2.4) and (b) the app-side phase truth
 * `CRITERION_MINIMUM_ALLOWED_PHASE` (unchanged seed, pgTAP drift guard).
 *
 * v1-key superset invariant (holy, unit-test-fixed): all 23 v1 field keys
 * remain – no v1 key is renamed or deleted.
 */

/** `criteria_catalog.minimum_allowed_phase` (app-side phase truth, §4.4). */
export type MinimumAllowedPhase =
  | 'initial_review'
  | 'viewing'
  | 'document_verification';

export const MINIMUM_ALLOWED_PHASE_ORDER: Record<MinimumAllowedPhase, number> =
  {
    initial_review: 0,
    viewing: 1,
    document_verification: 2,
  };

/**
 * Expected `minimum_allowed_phase` per generator-mapped `criterion_key`.
 *
 * UNCHANGED by the pivot (Sanierungs-Spec §4.4/M6): all 14 entries stay, the
 * DB seed `20260705120000` stays valid, the pgTAP drift guard
 * (`workspace_templates_drift.test.sql`) stays untouched. The mirror unit
 * test asserts (a) this table matches the documented seed, (b) every
 * catalog `criterionKeys` entry exists here, and (c) the directional
 * invariant `advicePhaseFloor(field) ≤ minimum_allowed_phase(criterion)`.
 */
export const CRITERION_MINIMUM_ALLOWED_PHASE: Record<
  string,
  MinimumAllowedPhase | null
> = {
  first_name_required: 'initial_review',
  last_name_required: 'initial_review',
  has_valid_phone_number: 'initial_review',
  household_size: 'viewing',
  has_employment: 'viewing',
  monthly_income_1_5x_rent: 'viewing',
  monthly_income_2x_rent: 'viewing',
  monthly_income_3x_rent: 'viewing',
  has_pets_false: 'viewing',
  income_proof_required: 'document_verification',
  credit_report_required: 'document_verification',
  creditworthiness_positive: 'document_verification',
  self_disclosure_required: null,
  signed_guarantor_form: null,
};

// ── Field interface (Spec §2.2) ───────────────────────────────────────────────

export type AdviceLevel = 'green' | 'yellow';
export type RecommendedFrom = 'after_viewing' | 'before_contract';
export type FieldScope = 'person' | 'household';

export type FieldKind =
  | 'text'
  | 'date'
  | 'checkbox'
  | 'checkbox_group'
  | 'yesno'
  | 'number'
  | 'textarea';

export type FieldSection =
  | 'personal'
  | 'household'
  | 'financial'
  | 'history'
  | 'documents';

/** Income capture: either the amount, or a confirmation of a threshold. */
export type IncomeVariant = 'amount' | 'threshold';

export interface LocalizedText {
  de: string;
  en: string;
}

/**
 * checkbox_group option: stable key + bilingual label (R3a – options labels
 * MUST carry DE/EN, otherwise `language: 'en'|'de-en'` is not renderable).
 */
export interface SelfDisclosureFieldOption {
  key: string;
  label: LocalizedText;
}

export interface FieldAdvice {
  level: AdviceLevel;
  /** Mandatory for yellow. */
  recommendedFrom?: RecommendedFrom;
  /**
   * Short Du-Form reason for the UI. Mandatory for yellow.
   *
   * 🔴 Bis zum 23.08.2026 hiess dieses Feld `reasonDe` und trug nur Deutsch —
   * wie der ganze Generator. Eine Vermieterin mit englischer oder
   * französischer Spracheinstellung sah 38 Feldnamen, 18 Begründungen und alle
   * Hinweise auf Deutsch (§1.25). Jetzt zweisprachig wie `label` und `hint`.
   */
  reason?: LocalizedText;
}

/**
 * Phase-hint labels for yellow advice, shown as a muted prefix on the field
 * card (Ampel-UI, UX-Bundle §7). Single source for both the marketing wizard
 * and the app-native generator.
 */
export const RECOMMENDED_FROM_LABEL: Record<RecommendedFrom, LocalizedText> = {
  after_viewing: {
    de: 'Empfohlen: nach der Besichtigung',
    en: 'Recommended: after the viewing',
  },
  before_contract: {
    de: 'Empfohlen: vor Vertragsabschluss',
    en: 'Recommended: before signing the contract',
  },
};

/**
 * Der zweisprachige Text in der Sprache der Oberfläche.
 *
 * 🔴 ZWEI SPRACHEN, DREI OBERFLÄCHEN-SPRACHEN — und das ist kein Versehen.
 * Blatt Q zeichnet die Sprachwahl des FORMULARS als „Deutsch · English ·
 * Beides"; eine französische Fassung des Dokuments gibt es dort nicht, und sie
 * wäre auch keine Übersetzung, sondern ein anderes Rechtsgebiet (WBS, SCHUFA,
 * Räumungstitel, AGG). Was hier übersetzt wird, ist die BEDIENUNG.
 *
 * Für Französisch greift deshalb die Rückfallregel der App (`fallbackLng`):
 * Englisch. Das ist die vorhandene Regel, keine neue Erfindung — und allemal
 * besser als 38 deutsche Feldnamen für eine französischsprachige Vermieterin.
 */
export function textFuerSprache(
  text: LocalizedText | undefined,
  sprache: string,
): string | undefined {
  if (!text) return undefined;

  return sprache.startsWith('de') ? text.de : text.en;
}

/**
 * Sprache des ERZEUGTEN Formulars — die drei Werte, die der Generator anbietet
 * („Deutsch · English · Beides", Blatt Q2).
 *
 * 🔴 Nicht zu verwechseln mit der Oberflächen-Sprache, nach der
 * `textFuerSprache` greift. Die Oberfläche kann deutsch sein, während das
 * Formular englisch entsteht — und umgekehrt.
 *
 * Der Typ steht hier und nicht in `config.schema`, weil dieses Modul dorthin
 * nicht zeigen darf (die Abhängigkeit läuft andersherum). Er läuft nicht
 * auseinander: aufgerufen wird `zweisprachigerText` ausschliesslich mit
 * `draft.language` bzw. `config.language`, und wächst der Zod-Aufzähler dort,
 * bricht der Aufruf hier beim Übersetzen.
 */
export type FormularSprache = 'de' | 'en' | 'de-en';

/**
 * Ein zweisprachiger Text in der Sprache des Formulars — bei „Beides" beide,
 * getrennt durch den Mittelpunkt, wie das Blatt die Doppelangabe schreibt.
 */
export function zweisprachigerText(
  text: LocalizedText,
  sprache: FormularSprache,
): string {
  if (sprache === 'en') return text.en;

  if (sprache === 'de-en') return `${text.de} · ${text.en}`;

  return text.de;
}

export interface SelfDisclosureField {
  /**
   * Stable field key. AcroForm name = `${key}_p{n}` (scope person) resp.
   * `${key}` (scope household) – underscore delimiter, NO dot (R11, §2.5).
   */
  key: string;
  /** person = own answer per person; household = one merged answer. */
  scope: FieldScope;
  advice: FieldAdvice;
  /**
   * Mapping onto `criteria_catalog.criterion_key` (existence test §4.4;
   * multi-mapping solves M6). Replaces the v1 singular `criterionKey`.
   */
  criterionKeys?: readonly string[];
  kind: FieldKind;
  options?: readonly SelfDisclosureFieldOption[];
  section: FieldSection;
  label: LocalizedText;
  /** PDF hint, Sie-Form, applicant-facing (rendered into the form). */
  hint?: LocalizedText;
  /** UI hint, Du-Form, landlord-facing (generator ONLY – D5 separation). */
  uiHint?: LocalizedText;
  /** "freiwillig" marker as in the DSK questionnaire. */
  optionalForApplicant?: boolean;
  /** Only meaningful when the sender header marks a social-housing unit. */
  socialHousingOnly?: boolean;
  /** For the two mutually-exclusive income capture variants. */
  incomeVariant?: IncomeVariant;
}

export const FIELD_SECTION_ORDER: readonly FieldSection[] = [
  'personal',
  'household',
  'financial',
  'history',
  'documents',
];

/** Section headings, oriented at the reference form (Spec §2.2). */
export const FIELD_SECTION_LABELS: Record<FieldSection, LocalizedText> = {
  personal: { de: 'Persönliches', en: 'Personal details' },
  household: { de: 'Haushalt & Nutzung', en: 'Household & use' },
  financial: {
    de: 'Einkommen & Beschäftigung',
    en: 'Income & employment',
  },
  history: {
    de: 'Bisheriges Mietverhältnis & Bonität',
    en: 'Current tenancy & creditworthiness',
  },
  documents: { de: 'Nachweise', en: 'Supporting documents' },
};

// ── Catalog (Spec §2.2 table – normative) ─────────────────────────────────────

/**
 * IMPORTANT: no field may reference any of the 18 forbidden categories
 * (`FORBIDDEN_CATEGORIES`). Adding one breaks `nie-list.test.ts`. Only ONE
 * textarea exists (the exculpation free-text field); adding any other
 * free-text impression field is forbidden.
 */
export const SELF_DISCLOSURE_FIELDS: readonly SelfDisclosureField[] = [
  // ── Persönliches ────────────────────────────────────────────────────────────
  {
    key: 'last_name',
    scope: 'person',
    advice: { level: 'green' },
    criterionKeys: ['last_name_required'],
    kind: 'text',
    section: 'personal',
    label: { de: 'Name', en: 'Surname' },
  },
  {
    key: 'first_name',
    scope: 'person',
    advice: { level: 'green' },
    criterionKeys: ['first_name_required'],
    kind: 'text',
    section: 'personal',
    label: { de: 'Vorname', en: 'First name' },
  },
  {
    key: 'birth_date',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'before_contract',
      reason: {
        de: 'Fürs Kennenlernen reicht der Name – das Geburtsdatum brauchst du erst für den Mietvertrag.',
        en: 'Getting to know someone takes only the name – you need the date of birth for the tenancy agreement.',
      },
    },
    kind: 'date',
    section: 'personal',
    label: { de: 'Geburtsdatum', en: 'Date of birth' },
    hint: {
      de: 'Diese Angabe ist freiwillig.',
      en: 'Providing this information is voluntary.',
    },
    optionalForApplicant: true,
  },
  {
    key: 'contact_address',
    scope: 'person',
    advice: { level: 'green' },
    kind: 'text',
    section: 'personal',
    label: { de: 'Anschrift', en: 'Address' },
    hint: {
      de: 'Bitte geben Sie mindestens eine Kontaktmöglichkeit an; weitere Angaben sind freiwillig.',
      en: 'Please provide at least one way to contact you; any further details are voluntary.',
    },
    optionalForApplicant: true,
  },
  {
    key: 'contact_phone',
    scope: 'person',
    advice: { level: 'green' },
    criterionKeys: ['has_valid_phone_number'],
    kind: 'text',
    section: 'personal',
    label: { de: 'Telefon', en: 'Phone' },
    optionalForApplicant: true,
  },
  {
    key: 'contact_email',
    scope: 'person',
    advice: { level: 'green' },
    kind: 'text',
    section: 'personal',
    label: { de: 'E-Mail', en: 'Email' },
    optionalForApplicant: true,
  },
  {
    key: 'wbs_available',
    scope: 'household',
    advice: { level: 'green' },
    kind: 'yesno',
    section: 'personal',
    label: {
      de: 'Wohnberechtigungsschein (WBS) vorhanden?',
      en: 'Do you hold a public-housing eligibility certificate (WBS)?',
    },
    hint: {
      de: 'Nur bei einer öffentlich geförderten Wohnung relevant.',
      en: 'Only relevant for publicly subsidised housing.',
    },
    socialHousingOnly: true,
  },

  // ── Haushalt & Nutzung ──────────────────────────────────────────────────────
  {
    key: 'household_total',
    scope: 'household',
    advice: { level: 'green' },
    criterionKeys: ['household_size'],
    kind: 'number',
    section: 'household',
    label: {
      de: 'Die Wohnung wird für folgende Anzahl von Personen benötigt',
      en: 'The apartment is required for the following number of persons',
    },
  },
  {
    key: 'household_adults',
    scope: 'household',
    advice: { level: 'green' },
    criterionKeys: ['household_size'],
    kind: 'number',
    section: 'household',
    label: {
      de: 'Anzahl einziehender Erwachsener',
      en: 'Number of adults moving in',
    },
  },
  {
    key: 'household_children',
    scope: 'household',
    advice: { level: 'green' },
    kind: 'number',
    section: 'household',
    label: {
      de: 'Anzahl einziehender Kinder',
      en: 'Number of children moving in',
    },
  },
  {
    key: 'commercial_use',
    scope: 'household',
    advice: { level: 'green' },
    kind: 'yesno',
    section: 'household',
    label: {
      de: 'Ist eine (teil-)gewerbliche Nutzung der Wohnung beabsichtigt?',
      en: 'Is (partly) commercial use of the apartment intended?',
    },
  },
  {
    key: 'pet_keeping',
    scope: 'household',
    advice: { level: 'green' },
    criterionKeys: ['has_pets_false'],
    kind: 'yesno',
    section: 'household',
    label: {
      de: 'Ist eine zustimmungsbedürftige Tierhaltung geplant?',
      en: 'Is pet keeping that requires the landlord’s approval planned?',
    },
    hint: {
      de: 'Nur soweit die Tierhaltung zustimmungsbedürftig ist; Kleintierhaltung ist ohne Zustimmung zulässig.',
      en: 'Only insofar as the pet keeping requires approval; keeping small animals is permitted without approval.',
    },
  },

  // ── Einkommen & Beschäftigung ───────────────────────────────────────────────
  {
    key: 'employment_status',
    scope: 'person',
    advice: { level: 'green' },
    kind: 'checkbox_group',
    section: 'financial',
    label: {
      de: 'Derzeitiger Beschäftigungsstatus',
      en: 'Current employment status',
    },
    // Deliberately NO employment DURATION option (stays red, category 9).
    options: [
      {
        key: 'befristet_angestellt',
        label: { de: 'Befristet angestellt', en: 'Employed (fixed-term)' },
      },
      {
        key: 'unbefristet_angestellt',
        label: { de: 'Unbefristet angestellt', en: 'Employed (permanent)' },
      },
      {
        key: 'selbststaendig',
        label: { de: 'Selbstständig', en: 'Self-employed' },
      },
      { key: 'verbeamtet', label: { de: 'Verbeamtet', en: 'Civil servant' } },
      {
        key: 'in_ausbildung_studium',
        label: {
          de: 'In Ausbildung / Studium',
          en: 'In vocational training / studies',
        },
      },
      { key: 'arbeitslos', label: { de: 'Arbeitslos', en: 'Unemployed' } },
      { key: 'rentner', label: { de: 'Rentner', en: 'Retired' } },
      { key: 'sonstiges', label: { de: 'Sonstiges', en: 'Other' } },
    ],
  },
  {
    key: 'employer',
    scope: 'person',
    advice: { level: 'green' },
    criterionKeys: ['has_employment'],
    kind: 'text',
    section: 'financial',
    label: { de: 'Derzeitiger Arbeitgeber', en: 'Current employer' },
    // Deliberately no employment-duration hint (red category 9): the mere fact
    // of employment is asked, not how long it has lasted.
  },
  {
    key: 'occupation',
    scope: 'person',
    advice: { level: 'green' },
    kind: 'text',
    section: 'financial',
    label: { de: 'Ausgeübter Beruf', en: 'Occupation' },
  },
  {
    key: 'net_income_amount',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'after_viewing',
      reason: {
        de: 'Für die Vorauswahl genügt die Bestätigung einer Betragsgrenze – den exakten Betrag brauchst du erst, wenn es konkret wird.',
        en: 'For the pre-selection a confirmed threshold is enough – you only need the exact amount once things get concrete.',
      },
    },
    // M6: the exact amount has no 1:1 criterion – no mapping (Spec §4.4).
    kind: 'number',
    section: 'financial',
    label: {
      de: 'Monatliches Nettoeinkommen (Euro)',
      en: 'Monthly net income (euros)',
    },
    hint: {
      de: 'Alternativ genügt die Bestätigung, dass eine Betragsgrenze erreicht wird.',
      en: 'Alternatively, confirming that a threshold amount is met is sufficient.',
    },
    incomeVariant: 'amount',
  },
  {
    key: 'income_threshold_met',
    scope: 'household',
    advice: { level: 'green' },
    criterionKeys: [
      'monthly_income_1_5x_rent',
      'monthly_income_2x_rent',
      'monthly_income_3x_rent',
    ],
    kind: 'yesno',
    section: 'financial',
    label: {
      de: 'Das monatliche Haushaltsnettoeinkommen erreicht die Betragsgrenze',
      en: 'The monthly net household income meets the threshold amount',
    },
    incomeVariant: 'threshold',
  },
  {
    key: 'social_benefits',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'after_viewing',
      reason: {
        de: 'Für den ersten Überblick brauchst du das nicht – relevant wird die Zahlungsfähigkeit erst, wenn nach der Besichtigung ernsthaftes Interesse besteht.',
        en: 'You do not need this for a first overview – solvency only becomes relevant once there is serious interest after the viewing.',
      },
    },
    kind: 'yesno',
    section: 'financial',
    label: {
      de: 'Werden Sozialleistungen bezogen?',
      en: 'Are social benefits being received?',
    },
  },

  // ── Bisheriges Mietverhältnis & Bonität ─────────────────────────────────────
  {
    key: 'consumer_insolvency',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'after_viewing',
      reason: {
        de: 'Zulässig wegen deines berechtigten Interesses, das Zahlungsausfall-Risiko einzuschätzen – sinnvoll erst, wenn nach der Besichtigung ernsthaftes Interesse besteht.',
        en: 'Admissible because of your legitimate interest in assessing the risk of payment default – but only sensible once there is serious interest after the viewing.',
      },
    },
    kind: 'yesno',
    section: 'history',
    label: {
      de: 'Besteht ein offenes Verbraucherinsolvenzverfahren?',
      en: 'Are there ongoing consumer insolvency proceedings?',
    },
  },
  {
    key: 'enforcement_measures',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'after_viewing',
      reason: {
        de: 'Wie die Insolvenz-Frage: berechtigtes Interesse am Zahlungsausfall-Risiko – stell sie erst nach der Besichtigung.',
        en: 'Like the insolvency question: legitimate interest in the risk of payment default – ask it only after the viewing.',
      },
    },
    kind: 'yesno',
    section: 'history',
    label: {
      de: 'Bestanden in den letzten fünf Jahren Vollstreckungsmaßnahmen (z. B. Abgabe einer Vermögensauskunft)?',
      en: 'Have there been enforcement measures within the last five years (e.g. a statutory declaration of assets)?',
    },
  },
  {
    key: 'eviction_title',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'after_viewing',
      reason: {
        de: 'Zulässig wegen deines berechtigten Interesses am Zahlungsausfall-Risiko – empfohlen erst nach der Besichtigung.',
        en: 'Admissible because of your legitimate interest in the risk of payment default – recommended only after the viewing.',
      },
    },
    kind: 'yesno',
    section: 'history',
    label: {
      de: 'Besteht ein Räumungstitel wegen Mietrückständen aus den letzten fünf Jahren?',
      en: 'Is there an eviction order due to rent arrears from the last five years?',
    },
  },
  {
    key: 'rent_arrears_current',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'before_contract',
      reason: {
        de: 'Mietrückstände darfst du erfragen – aber erst kurz vor dem Vertragsabschluss, wenn die Auswahl konkret wird.',
        en: 'You may ask about rent arrears – but only shortly before signing, when the choice has narrowed down.',
      },
    },
    kind: 'yesno',
    section: 'history',
    label: {
      de: 'Bestehen gegenüber Ihrem derzeitigen Vermieter Mietrückstände?',
      en: 'Are there currently rent arrears towards your current landlord?',
    },
  },
  {
    key: 'rent_arrears_past',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'before_contract',
      reason: {
        de: 'Mietrückstände darfst du erfragen – aber erst kurz vor dem Vertragsabschluss, wenn die Auswahl konkret wird.',
        en: 'You may ask about rent arrears – but only shortly before signing, when the choice has narrowed down.',
      },
    },
    kind: 'yesno',
    section: 'history',
    label: {
      de: 'Bestanden in den letzten fünf Jahren Mietrückstände gegenüber früheren Vermietern?',
      en: 'Were there rent arrears towards former landlords within the last five years?',
    },
  },
  {
    key: 'termination_breach',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'before_contract',
      reason: {
        de: 'Frag das erst kurz vor dem Vertragsabschluss – vorher ist die Angabe nicht erforderlich.',
        en: 'Ask this only shortly before signing – before that the information is not necessary.',
      },
    },
    kind: 'yesno',
    section: 'history',
    label: {
      de: 'Wurde ein Mietverhältnis wegen einer erheblichen Pflichtverletzung gekündigt?',
      en: 'Has a tenancy been terminated due to a significant breach of obligations?',
    },
  },
  {
    key: 'termination_breach_explanation',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'before_contract',
      reason: {
        de: 'Das Freitext-Feld dient allein der Entlastung des Bewerbers – nimm es zusammen mit der Frage zur Kündigung auf.',
        en: 'The free-text field exists solely so the applicant can explain themselves – include it together with the termination question.',
      },
    },
    kind: 'textarea',
    section: 'history',
    label: {
      de: 'Ergänzende Erläuterung (optional)',
      en: 'Additional explanation (optional)',
    },
    hint: {
      de: 'Das Ausfüllen dieses Feldes ist freiwillig und dient ausschließlich Ihrer Entlastung.',
      en: 'Completing this field is voluntary and serves solely to provide context in your favour.',
    },
    optionalForApplicant: true,
  },

  // ── Nachweise ───────────────────────────────────────────────────────────────
  {
    key: 'income_proof',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'before_contract',
      reason: {
        de: 'Nachweise gehören ans Ende des Auswahlprozesses – direkt vor den Vertragsabschluss.',
        en: 'Supporting documents belong at the end of the selection process – right before signing.',
      },
    },
    criterionKeys: ['income_proof_required'],
    kind: 'checkbox',
    section: 'documents',
    label: {
      de: 'Einkommensnachweis wird beigelegt',
      en: 'Proof of income is enclosed',
    },
    hint: {
      de: 'Nicht erforderliche Angaben (etwa einzelne Kontobewegungen) dürfen vor der Übermittlung unkenntlich gemacht werden.',
      en: 'Details that are not required (such as individual account transactions) may be redacted before submission.',
    },
    uiHint: {
      de: 'Bewerber dürfen nicht erforderliche Angaben schwärzen – der Hinweis dazu steht im PDF.',
      en: 'Applicants may redact information that is not necessary – the note about this is in the PDF.',
    },
  },
  {
    key: 'credit_report',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'before_contract',
      reason: {
        de: 'Nachweise gehören ans Ende des Auswahlprozesses – direkt vor den Vertragsabschluss.',
        en: 'Supporting documents belong at the end of the selection process – right before signing.',
      },
    },
    criterionKeys: ['credit_report_required'],
    kind: 'checkbox',
    section: 'documents',
    label: {
      de: 'Selbst eingeholter, zweckgebundener Bonitätsnachweis wird beigelegt',
      en: 'A self-obtained, purpose-bound credit report is enclosed',
    },
    hint: {
      de: 'Bitte legen Sie einen Nachweis nur vor, soweit er für die Auswahlentscheidung erforderlich ist; ein selbst eingeholter, zweckgebundener Nachweis genügt.',
      en: 'Please provide a report only insofar as it is required for the selection decision; a self-obtained, purpose-bound report is sufficient.',
    },
    uiHint: {
      de: 'Ein selbst eingeholter, zweckgebundener Bonitätsnachweis genügt – der Hinweis dazu steht im PDF.',
      en: 'A self-obtained, purpose-bound credit report is enough – the note about this is in the PDF.',
    },
  },
  {
    key: 'public_cost_coverage',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'before_contract',
      reason: {
        de: 'Nachweise gehören ans Ende des Auswahlprozesses – direkt vor den Vertragsabschluss.',
        en: 'Supporting documents belong at the end of the selection process – right before signing.',
      },
    },
    kind: 'checkbox',
    section: 'documents',
    label: {
      de: 'Nachweis über eine öffentliche Kostenübernahme wird beigelegt',
      en: 'Proof of public coverage of housing costs is enclosed',
    },
    uiHint: {
      de: 'Nur sinnvoll, wenn eine öffentliche Stelle die Miete übernimmt.',
      en: 'Only meaningful when a public body covers the rent.',
    },
  },
  {
    key: 'wbs_copy',
    scope: 'person',
    advice: {
      level: 'yellow',
      recommendedFrom: 'before_contract',
      reason: {
        de: 'Nachweise gehören ans Ende des Auswahlprozesses – direkt vor den Vertragsabschluss.',
        en: 'Supporting documents belong at the end of the selection process – right before signing.',
      },
    },
    kind: 'checkbox',
    section: 'documents',
    label: {
      de: 'Kopie des Wohnberechtigungsscheins wird beigelegt',
      en: 'A copy of the public-housing eligibility certificate (WBS) is enclosed',
    },
    hint: {
      de: 'Nur bei einer öffentlich geförderten Wohnung relevant.',
      en: 'Only relevant for publicly subsidised housing.',
    },
    uiHint: {
      de: 'Nur sinnvoll bei einer öffentlich geförderten Wohnung.',
      en: 'Only meaningful for a publicly subsidised apartment.',
    },
    socialHousingOnly: true,
  },
];

const FIELDS_BY_KEY: ReadonlyMap<string, SelfDisclosureField> = new Map(
  SELF_DISCLOSURE_FIELDS.map((field) => [field.key, field]),
);

export function getFieldByKey(key: string): SelfDisclosureField | undefined {
  return FIELDS_BY_KEY.get(key);
}

/**
 * Die EINE Ampel-Rechnung über einer Menge von Feldschlüsseln.
 *
 * 🔴 Es gab zwei. `presetAmpel` rechnete sie für die drei Sätze im Generator,
 * `hatGelbeFelder` in der Vorlagen-Liste noch einmal für eine gespeicherte
 * Vorlage — zeichengleiche Regel, zwei Stellen. Zwei Ampel-Wahrheiten heisst:
 * eine kann sich ändern und die andere nicht, und dann sagt derselbe Feldsatz
 * im Generator etwas anderes als in der Liste. Beide gehen jetzt hier durch.
 *
 * Blatt Q5: „Der Punkt davor sagt, ob die Vorlage NUR grüne Felder enthält
 * oder auch gelbe — also ob man sie schon bei der ersten Anfrage verschicken
 * kann oder erst später."
 *
 * Die Stufe ist deshalb die SCHÄRFERE der enthaltenen Felder, nicht die
 * häufigere: ein einziges gelbes Feld verschiebt den frühesten
 * Versandzeitpunkt der ganzen Vorlage. Wer die Ampel mittelte, machte aus
 * einer Warnung eine Statistik.
 *
 * Ein unbekannter Schlüssel (etwa aus einer älteren Fassung) zählt NICHT mit,
 * statt zu werfen: eine Vorlage gelb zu färben, weil wir einen Schlüssel nicht
 * kennen, wäre eine Behauptung über ihren Inhalt.
 */
export function ampelFuerFeldSchluessel(
  schluessel: readonly string[] | undefined,
): AdviceLevel {
  const gelb = (schluessel ?? []).some(
    (key) => FIELDS_BY_KEY.get(key)?.advice.level === 'yellow',
  );

  return gelb ? 'yellow' : 'green';
}

/**
 * Directional invariant helper (Spec §4.4/R2): the earliest app phase a
 * field's advice level maps onto. Semantics: a question may be ASKED earlier
 * than the criterion may be USED for selection – never the other way around.
 */
export function advicePhaseFloor(
  field: SelfDisclosureField,
): MinimumAllowedPhase {
  if (field.advice.level === 'green') return 'initial_review';

  return field.advice.recommendedFrom === 'before_contract'
    ? 'document_verification'
    : 'viewing';
}

// ── Forbidden categories (red section, Spec §2.2 + R5) ────────────────────────

export interface ForbiddenCategory {
  key: string;
  label: LocalizedText;
  /**
   * One-liner "why not", zweisprachig.
   *
   * 🔴 Hier lagen `reasonDe` und `reasonEn` als ZWEI Felder nebeneinander —
   * die Oberfläche las immer das erste. Beide Texte gab es also längst; sie
   * standen nur so da, dass niemand die Wahl hatte (§1.25).
   */
  reason: LocalizedText;
}

/**
 * The 18 red categories (former 16-rule NIE list minus the fields that turned
 * yellow per E3, plus the R5 AGG core categories 17+18). Rendered in the UI
 * as the non-selectable section "Das darfst du nicht fragen"; the renderer
 * can structurally never produce them (asserted by `nie-list.test.ts`).
 *
 * NOTE: this constant contains the signal words BY DEFINITION – the NIE scan
 * scope is strictly `SELF_DISCLOSURE_FIELDS` + rendered output, never this
 * module as a whole (Spec S2).
 */
export const FORBIDDEN_CATEGORIES: readonly ForbiddenCategory[] = [
  {
    key: 'marital_status',
    label: { de: 'Familienstand', en: 'Marital status' },
    reason: {
      de: 'AGG-geschützt und für die Vermietung nicht erforderlich – wie viele Personen einziehen, fragst du über die Haushaltsgröße.',
      en: 'Protected under anti-discrimination law and not required – ask about household size instead.',
    },
  },
  {
    key: 'gender',
    label: { de: 'Geschlecht', en: 'Gender' },
    reason: {
      de: 'AGG-Kernkategorie – das Geschlecht ist für die Vermietungsentscheidung nie erforderlich.',
      en: 'A core anti-discrimination category – never required for a letting decision.',
    },
  },
  {
    key: 'religion',
    label: { de: 'Religion & Weltanschauung', en: 'Religion & beliefs' },
    reason: {
      de: 'Besondere Datenkategorie nach Art. 9 DSGVO und AGG-geschützt – tabu.',
      en: 'A special category of data under Art. 9 GDPR and protected under anti-discrimination law – off limits.',
    },
  },
  {
    key: 'origin_nationality',
    label: {
      de: 'Herkunft & Staatsangehörigkeit',
      en: 'Ethnic origin & nationality',
    },
    reason: {
      de: 'AGG-Kernkategorie – Herkunft und Staatsangehörigkeit dürfen keine Rolle spielen.',
      en: 'A core anti-discrimination category – origin and nationality must play no role.',
    },
  },
  {
    key: 'pregnancy_family_planning',
    label: {
      de: 'Schwangerschaft & Familienplanung',
      en: 'Pregnancy & family planning',
    },
    reason: {
      de: 'Höchstpersönliche Lebensplanung – die Frage ist unzulässig und darf unwahr beantwortet werden.',
      en: 'Strictly private life planning – the question is inadmissible and may lawfully be answered untruthfully.',
    },
  },
  {
    key: 'criminal_record',
    label: {
      de: 'Vorstrafen & Ermittlungsverfahren',
      en: 'Criminal record & investigations',
    },
    reason: {
      de: 'Für ein Mietverhältnis nicht erforderlich – pauschale Fragen danach sind unzulässig.',
      en: 'Not required for a tenancy – blanket questions about it are inadmissible.',
    },
  },
  {
    key: 'memberships',
    label: {
      de: 'Mitgliedschaften (Gewerkschaft, Partei, Verein)',
      en: 'Memberships (trade union, party, club)',
    },
    reason: {
      de: 'Besondere Datenkategorien bzw. Privatsache – ohne jeden Bezug zur Vermietung.',
      en: 'Special data categories or private matters – with no relation to the tenancy.',
    },
  },
  {
    key: 'smoking_status',
    label: { de: 'Raucher-Status', en: 'Smoking status' },
    reason: {
      de: 'Private Lebensführung – nach der DSK-Orientierungshilfe nicht abfragbar.',
      en: 'Private lifestyle – not permissible to ask according to the DSK guidance.',
    },
  },
  {
    key: 'employment_duration',
    label: {
      de: 'Beschäftigungsdauer & Probezeit',
      en: 'Length of employment & probation period',
    },
    reason: {
      de: 'Gefragt werden darf, OB eine Beschäftigung besteht – nicht, wie lange schon oder ob sie noch in der Probezeit ist.',
      en: 'You may ask WHETHER someone is employed – not for how long or whether they are still on probation.',
    },
  },
  {
    key: 'claim_reasons',
    label: {
      de: 'Gründe bestehender Forderungen',
      en: 'Reasons behind existing claims',
    },
    reason: {
      de: 'Das Ausforschen von Forderungsgründen geht über das berechtigte Interesse hinaus.',
      en: 'Probing the reasons behind claims goes beyond the legitimate interest.',
    },
  },
  {
    key: 'previous_landlord_contact',
    label: {
      de: 'Kontaktdaten von Vormieter / Vorvermieter',
      en: 'Contact details of previous tenants / previous landlords',
    },
    reason: {
      de: 'Kontaktdaten Dritter darfst du nicht abfragen – der Bewerber kann sie dir gar nicht wirksam freigeben.',
      en: 'You may not request third-party contact details – the applicant cannot validly share them.',
    },
  },
  {
    key: 'id_documents',
    label: {
      de: 'Ausweisdaten & Ausweiskopie',
      en: 'Identity card data & copies',
    },
    reason: {
      de: 'Ausweisdaten und -kopien sind für die Bewerberauswahl nicht erforderlich.',
      en: 'Identity card data and copies are not required for selecting an applicant.',
    },
  },
  {
    key: 'rent_debt_clearance_certificate',
    label: {
      de: 'Mietschuldenfreiheitsbescheinigung',
      en: 'Certificate of no rent arrears',
    },
    reason: {
      de: 'Auf diese Bescheinigung besteht kein Anspruch – die Ja/Nein-Fragen zu Rückständen genügen.',
      en: 'There is no entitlement to this certificate – the yes/no questions about arrears are sufficient.',
    },
  },
  {
    key: 'data_copy_art15',
    label: {
      de: 'Datenkopie nach Art. 15 DSGVO',
      en: 'Data copy under Art. 15 GDPR',
    },
    reason: {
      de: 'Die Auskunfts-Datenkopie ist ein Betroffenenrecht – sie als Bewerbungsunterlage zu verlangen ist zweckwidrig.',
      en: 'The data copy is a data-subject right – demanding it as an application document is contrary to its purpose.',
    },
  },
  {
    key: 'consent_clauses',
    label: {
      de: 'Einwilligungs- & SCHUFA-Klauseln',
      en: 'Declarations of consent & SCHUFA clauses',
    },
    reason: {
      de: 'Vorformulierte Einwilligungen tragen die Abfrage nicht – die Selbstauskunft stützt sich auf die Vertragsanbahnung.',
      en: 'Pre-formulated declarations do not support the questionnaire – it relies on the initiation of the contract.',
    },
  },
  {
    key: 'free_text_impressions',
    label: {
      de: 'Freitext-Eindrucksfelder',
      en: 'Free-text impression fields',
    },
    reason: {
      de: 'Offene Eindrucks-Felder sammeln unkontrolliert Daten – das einzige Freitextfeld dient der Entlastung des Bewerbers.',
      en: 'Open impression fields collect data without limits – the only free-text field exists to let applicants add context in their favour.',
    },
  },
  {
    key: 'disability_health',
    label: {
      de: 'Behinderung & Gesundheitszustand',
      en: 'Disability & state of health',
    },
    reason: {
      de: 'AGG-Kernkategorie und besondere Datenkategorie – für die Vermietung nie erforderlich.',
      en: 'A core anti-discrimination category and special data category – never required for letting.',
    },
  },
  {
    key: 'sexual_identity',
    label: {
      de: 'Sexuelle Identität & Orientierung',
      en: 'Sexual identity & orientation',
    },
    reason: {
      de: 'AGG-Kernkategorie – ohne jeden Bezug zur Vermietung, die Frage ist unzulässig.',
      en: 'A core anti-discrimination category – with no relation to the tenancy, the question is inadmissible.',
    },
  },
];

// ── Presets (UI shortcuts, NOT part of the config – Spec §2.4) ────────────────

export type SelfDisclosurePresetKey =
  | 'basis'
  | 'nach_besichtigung'
  | 'vor_vertragsabschluss';

/**
 * 🔴 `key` ist gespeicherter Bestand, kein Anzeigetext. Er wird nicht
 * umbenannt — er steckt in `detectPresetKey`, in den `data-test`-Haken der
 * drei Karten und damit in bestehenden Vorlagen-Abläufen.
 *
 * `nameEn`/`descriptionEn` sind ADDITIV ergänzt (Blatt Q3b/Q4): der Generator
 * bietet „Deutsch · English · Beides" an, die drei Sätze trugen aber nur
 * Deutsch. Wer ein englisches Formular baute, las den einzigen Wegweiser
 * dieses Bildschirms in einer Sprache, die er gerade abgewählt hatte.
 */
export interface SelfDisclosurePreset {
  key: SelfDisclosurePresetKey;
  nameDe: string;
  nameEn: string;
  /** Du-Form, one sentence. */
  descriptionDe: string;
  descriptionEn: string;
  fieldKeys: readonly string[];
}

/**
 * The two income-capture variants are mutually exclusive (config superRefine
 * relies on the UI enforcing it). Presets deterministically prefer the
 * data-minimal threshold confirmation (green) over the exact amount (yellow);
 * users can still swap manually afterwards.
 */
function withoutConflictingIncomeVariant(keys: string[]): string[] {
  const hasThreshold = keys.some(
    (key) => getFieldByKey(key)?.incomeVariant === 'threshold',
  );

  if (!hasThreshold) return keys;

  return keys.filter((key) => getFieldByKey(key)?.incomeVariant !== 'amount');
}

function presetKeys(
  predicate: (field: SelfDisclosureField) => boolean,
): readonly string[] {
  return withoutConflictingIncomeVariant(
    SELF_DISCLOSURE_FIELDS.filter(predicate).map((field) => field.key),
  );
}

/**
 * ===========================================================================
 * 🔴 ZWEI GEDANKENSTRICHE UND EIN FREMDWORT — 602 Punkte 3 und 6
 * ===========================================================================
 *
 *   „3 · Zwei Gedankenstriche auf der Fläche (418): «ersten Überblick – die
 *    datensparsamste» und «Nachweisen – für die finale». Beide werden ein
 *    Punkt, der Folgesatz beginnt groß."
 *
 *   „6 · «finale Entscheidung» → «endgültige Entscheidung»."
 *
 * 418 unterscheidet die beiden Verwendungen: der Gedankenstrich trennt zwei
 * ANGABEN, nicht zwei SÄTZE. Hier standen jeweils zwei Sätze.
 *
 * ⚠️ Die Sätze der Vorgabe stehen HIER und nicht im Sprachkatalog — sie
 * gehören zur Vorgabe selbst (`descriptionDe`/`descriptionEn`), und
 * `presetBeschreibung` wählt danach die Sprache aus.
 *
 * Preset field sets per Spec §2.4 (speaking names, no phase letters – E5).
 * A preset is a UI shortcut that pre-fills `enabledFields`; the field set is
 * the only truth. Preset application in the UI runs through the same
 * sanitizing as manual toggles (socialHousingOnly / publicPayerDirect).
 */
export const SELF_DISCLOSURE_PRESETS: readonly SelfDisclosurePreset[] = [
  {
    key: 'basis',
    nameDe: 'Basis (Kontakt & Haushalt)',
    nameEn: 'Basics (contact & household)',
    descriptionDe:
      'Kontakt- und Haushaltsangaben für den ersten Überblick. Die datensparsamste Variante.',
    descriptionEn:
      'Contact and household details for a first overview. The most data-minimal option.',
    fieldKeys: presetKeys(
      (field) =>
        field.advice.level === 'green' &&
        (field.section === 'personal' || field.section === 'household'),
    ),
  },
  {
    key: 'nach_besichtigung',
    nameDe: 'Nach der Besichtigung (empfohlen)',
    nameEn: 'After the viewing (recommended)',
    descriptionDe:
      'Alle unbedenklichen Felder plus die Angaben, die nach der Besichtigung empfohlen sind.',
    descriptionEn:
      'All harmless fields plus the details that are recommended from the viewing onwards.',
    fieldKeys: presetKeys(
      (field) =>
        field.advice.level === 'green' ||
        field.advice.recommendedFrom === 'after_viewing',
    ),
  },
  {
    key: 'vor_vertragsabschluss',
    nameDe: 'Vor Vertragsabschluss (vollständig)',
    nameEn: 'Before signing the contract (complete)',
    descriptionDe:
      'Die vollständige Selbstauskunft inklusive Nachweisen. Für die endgültige Entscheidung.',
    descriptionEn:
      'The complete self-disclosure including supporting documents. For the final decision.',
    fieldKeys: presetKeys(() => true),
  },
];

/**
 * Ampel-Stufe und Feldzahl eines Satzes — Blatt Q2.
 *
 * Q2: „Drei Sätze mit **Ampel-Punkt und Feldzahl** … jeder mit seiner
 * Ampel-Farbe und der Feldzahl. **So sieht man vor dem Klicken, was man
 * bekommt.**"
 *
 * 🔴 Genau das fehlte: die drei Karten trugen Namen und Satz, aber weder Punkt
 * noch Zahl — man klickte und sah erst danach, was der Satz anrichtet.
 *
 * Die Stufe ist die SCHÄRFERE der enthaltenen Felder, nicht die häufigere: ein
 * Satz mit neunzehn unbedenklichen und einem gelben Feld ist ein gelber Satz.
 * Wer die Ampel mitteln würde, machte aus einer Warnung eine Statistik.
 */
export function presetAmpel(preset: SelfDisclosurePreset): {
  stufe: AdviceLevel;
  felder: number;
} {
  const felder = preset.fieldKeys
    .map((key) => FIELDS_BY_KEY.get(key))
    .filter((field): field is SelfDisclosureField => Boolean(field));

  return {
    // 🔴 Dieselbe Rechnung wie in der Vorlagen-Liste — EINE Quelle, sonst
    // sagt derselbe Feldsatz hier etwas anderes als dort.
    stufe: ampelFuerFeldSchluessel(felder.map((field) => field.key)),
    felder: felder.length,
  };
}

/**
 * Name eines Satzes in der Sprache des Formulars.
 *
 * Q3b: „Die Namen im Bestand sind besser — sie nennen den Zeitpunkt, nicht die
 * Menge, und der Zeitpunkt ist die eigentliche Entscheidung. Übernehmen, nicht
 * umbenennen."
 */
export function presetName(
  preset: SelfDisclosurePreset,
  sprache: FormularSprache,
): string {
  return zweisprachigerText({ de: preset.nameDe, en: preset.nameEn }, sprache);
}

/** Der erklärende Satz unter dem Namen, in der Sprache des Formulars. */
export function presetBeschreibung(
  preset: SelfDisclosurePreset,
  sprache: FormularSprache,
): string {
  return zweisprachigerText(
    { de: preset.descriptionDe, en: preset.descriptionEn },
    sprache,
  );
}

export function getPresetByKey(key: string): SelfDisclosurePreset | undefined {
  return SELF_DISCLOSURE_PRESETS.find((preset) => preset.key === key);
}
